import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: 'Phone',
      value: '(786) 304-7898',
      href: 'tel:+17863047898',
    },
    {
      icon: Mail,
      label: 'Email',
      value: 'flconcretesales@gmail.com',
      href: 'mailto:flconcretesales@gmail.com',
    },
    {
      icon: MapPin,
      label: 'Service Area',
      value: 'Across South Florida',
      href: null,
    },
    {
      icon: Clock,
      label: 'Hours',
      value: '24/7 Emergency Available',
      href: null,
    },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block font-heading text-sm font-semibold tracking-widest uppercase text-orange-500 mb-4">
            Get In Touch
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900 mb-6">
            Contact Us
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Ready to start your project? Have questions? Reach out and our team 
            will respond promptly.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                    Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    data-testid="contact-name-input"
                    className="rounded-none border-slate-300 focus:border-orange-500 focus:ring-orange-500 h-12"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                    Email *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    data-testid="contact-email-input"
                    className="rounded-none border-slate-300 focus:border-orange-500 focus:ring-orange-500 h-12"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
                  Phone
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(123) 456-7890"
                  data-testid="contact-phone-input"
                  className="rounded-none border-slate-300 focus:border-orange-500 focus:ring-orange-500 h-12"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                  Message *
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project..."
                  data-testid="contact-message-input"
                  className="rounded-none border-slate-300 focus:border-orange-500 focus:ring-orange-500 resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit-btn"
                className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-none px-8 py-6 font-bold uppercase tracking-wider text-base transition-all hover:shadow-[4px_4px_0px_0px_rgba(51,65,85,1)] disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="bg-slate-50 p-8 border-l-4 border-orange-500">
              <h3 className="font-heading text-2xl font-bold text-slate-900 mb-4">
                Quick Contact
              </h3>
              <p className="text-slate-600 mb-6">
                Need immediate assistance? Call us directly for the fastest response.
              </p>
              <a
                href="tel:+17863047898"
                className="inline-flex items-center gap-3 text-2xl font-bold text-orange-500 hover:text-orange-600 transition-colors"
              >
                <Phone className="h-6 w-6" />
                (786) 304-7898
              </a>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {contactInfo.map((item) => {
                const Icon = item.icon;
                const content = (
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-orange-100 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-orange-500" />
                    </div>
                    <div>
                      <div className="text-sm text-slate-500 font-medium mb-1">
                        {item.label}
                      </div>
                      <div className="font-semibold text-slate-900">
                        {item.value}
                      </div>
                    </div>
                  </div>
                );

                return item.href ? (
                  <a
                    key={item.label}
                    href={item.href}
                    className="block hover:bg-slate-50 p-4 -m-4 transition-colors"
                  >
                    {content}
                  </a>
                ) : (
                  <div key={item.label} className="p-4 -m-4">
                    {content}
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
